import { memo, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { activate } from "../../../../utils/keyboard-activate";
import type { BatchExtractionRecord } from '../../../../types/batch-extraction';
import { isCombinedBatsThrows, isValidBats, isValidThrows } from '../../../../constants/validation';
import { JUCO_YEARS, type JucoYear } from '../../../../utils/roster-class';
import type { TeamOption } from '../../../../components/TeamTypeahead';
import { RecordTeamAssignment } from './RecordTeamAssignment';

type EditableField = {
  key: keyof BatchExtractionRecord;
  label: string;
  width: string;
};

interface RecordRowProps {
  record: BatchExtractionRecord;
  editableFields: EditableField[];
  editingField: keyof BatchExtractionRecord | null;
  onStartEdit: (recordId: string, field: keyof BatchExtractionRecord) => void;
  onCommitEdit: (recordId: string, field: keyof BatchExtractionRecord, newValue: string) => void;
  onCancelEdit: () => void;
  onDelete: (recordId: string) => void;
  /** Render the leading per-record team-assignment cell. */
  showTeamAssignment: boolean;
  /**
   * Cached display name for the record's assigned team. The API returns only
   * the resolved team's Guid, so the parent caches the name when the operator
   * picks it. Falls back to the record's extracted `teamName` when absent.
   */
  assignedTeamName: string | null;
  /** True when the commit gate flagged this record as unresolved. */
  teamUnresolved: boolean;
  /** True when the commit gate flagged this record as JUCO with no JC year. */
  jucoYearMissing: boolean;
  onAssignTeam: (recordId: string, team: TeamOption) => void;
  onClearTeam: (recordId: string) => void;
}

export const RecordRow = memo(function RecordRow({
  record,
  editableFields,
  editingField,
  onStartEdit,
  onCommitEdit,
  onCancelEdit,
  onDelete,
  showTeamAssignment,
  assignedTeamName,
  teamUnresolved,
  jucoYearMissing,
  onAssignTeam,
  onClearTeam,
}: RecordRowProps) {
  const handleDelete = useCallback(() => {
    onDelete(record.id);
  }, [onDelete, record.id]);

  // Build a stable TeamOption from primitives so the memoized assignment cell
  // only re-renders when its underlying value actually changes.
  const assignedTeam = useMemo<TeamOption | null>(() => {
    if (!record.resolvedTeamId) return null;
    return {
      id: record.resolvedTeamId,
      name: assignedTeamName ?? record.resolvedTeamName ?? record.teamName ?? 'Assigned team',
      isNew: false,
    };
  }, [record.resolvedTeamId, assignedTeamName, record.resolvedTeamName, record.teamName]);

  return (
    <tr className="review-table__row">
      <td className="review-table__cell--actions">
        <button
          className="review-table__action-btn review-table__action-btn--delete"
          onClick={handleDelete}
          title="Delete record"
        >
          Del
        </button>
      </td>
      {showTeamAssignment && (
        <td className="review-table__cell review-table__cell--team-assignment">
          <RecordTeamAssignment
            recordId={record.id}
            value={assignedTeam}
            unresolved={teamUnresolved}
            onAssign={onAssignTeam}
            onClear={onClearTeam}
          />
        </td>
      )}
      {editableFields.map((field) => (
        <td
          key={field.key}
          className={`review-table__cell${
            field.key === 'jucoYear' && jucoYearMissing
              ? ' review-table__cell--juco-missing'
              : ''
          }`}
        >
          <RecordCell
            recordId={record.id}
            field={field.key}
            value={record[field.key]}
            isEditing={editingField === field.key}
            onStartEdit={onStartEdit}
            onCommitEdit={onCommitEdit}
            onCancelEdit={onCancelEdit}
          />
        </td>
      ))}
    </tr>
  );
}, (prevProps, nextProps) => {
  // Only re-render if this specific row's data or editing state changed
  if (prevProps.record !== nextProps.record) return false;
  if (prevProps.editingField !== nextProps.editingField) return false;
  if (prevProps.editableFields !== nextProps.editableFields) return false;
  if (prevProps.showTeamAssignment !== nextProps.showTeamAssignment) return false;
  if (prevProps.assignedTeamName !== nextProps.assignedTeamName) return false;
  if (prevProps.teamUnresolved !== nextProps.teamUnresolved) return false;
  if (prevProps.jucoYearMissing !== nextProps.jucoYearMissing) return false;
  // Callbacks are stable via useCallback in parent
  return true;
});

interface RecordCellProps {
  recordId: string;
  field: keyof BatchExtractionRecord;
  value: BatchExtractionRecord[keyof BatchExtractionRecord];
  isEditing: boolean;
  onStartEdit: (recordId: string, field: keyof BatchExtractionRecord) => void;
  onCommitEdit: (recordId: string, field: keyof BatchExtractionRecord, newValue: string) => void;
  onCancelEdit: () => void;
}

const RecordCell = memo(function RecordCell({
  recordId,
  field,
  value,
  isEditing,
  onStartEdit,
  onCommitEdit,
  onCancelEdit,
}: RecordCellProps) {
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Initialize edit value when entering edit mode
  useEffect(() => {
    if (isEditing) {
      setEditValue(value?.toString() ?? '');
      // Focus input after render
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [isEditing, value]);

  const handleBlur = useCallback(() => {
    onCommitEdit(recordId, field, editValue);
  }, [recordId, field, editValue, onCommitEdit]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onCommitEdit(recordId, field, editValue);
    } else if (e.key === 'Escape') {
      onCancelEdit();
    }
  }, [recordId, field, editValue, onCommitEdit, onCancelEdit]);

  const handleClick = useCallback(() => {
    onStartEdit(recordId, field);
  }, [recordId, field, onStartEdit]);

  // Format value for display - handle primitive types and null
  const displayValue = value === null || value === undefined
    ? '-'
    : typeof value === 'object'
      ? JSON.stringify(value)
      : String(value);

  // Check for validation warnings on Bats/Throws fields
  // Must be called before any early returns to follow Rules of Hooks
  const validationWarning = useMemo(() => {
    if (field === 'bats') {
      if (isCombinedBatsThrows(displayValue)) {
        return 'Combined value will be auto-split on save';
      }
      if (displayValue !== '-' && !isValidBats(displayValue)) {
        return 'Invalid value - use R, L, or S';
      }
    }
    if (field === 'throws') {
      if (displayValue !== '-' && !isValidThrows(displayValue)) {
        return 'Invalid value - use R or L';
      }
    }
    // There is no JC 4 — a two-year school does not have one.
    if (field === 'jucoYear' && displayValue !== '-' && !JUCO_YEARS.includes(displayValue as JucoYear)) {
      return 'Invalid value - use 1, 2 or 3';
    }
    return null;
  }, [field, displayValue]);

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        className="review-table__edit-input"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
    );
  }

  return (
    <span
      className={`review-table__cell-value${validationWarning ? ' review-table__cell-value--warning' : ''}`}
      {...activate(handleClick)}
      title={validationWarning || undefined}
    >
      {displayValue}
      {validationWarning && <span className="review-table__warning-icon">⚠</span>}
    </span>
  );
}, (prevProps, nextProps) => {
  // Only re-render if cell-specific data changed
  if (prevProps.value !== nextProps.value) return false;
  if (prevProps.isEditing !== nextProps.isEditing) return false;
  if (prevProps.recordId !== nextProps.recordId) return false;
  if (prevProps.field !== nextProps.field) return false;
  // Callbacks are stable via useCallback
  return true;
});
